/**
=========================================================
* BASE: Learning Repository - v1.0.0
=========================================================

* Copyright 2022 Sean Dooley (https://www.dooley-dev.com)

Coded by www.dooley-dev.com

 =========================================================

* The above copyright notice shall be included in substantial portions of the Software.
*/
// react and redux
import React, { useEffect, useState } from "react";
import { Link, useOutletContext, useParams, useLocation } from "react-router-dom";
// redux
import { useDispatch } from "react-redux";
// actions
import { getListing, addInstance, updateInstance, deleteInstance } from "../../../actions";
// forms
import * as yup from "yup";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup/dist/yup";
// BASE Components
import _ from "../../../../@lodash";
import BASELoading from "../../../../@BASE/core/BASELoading";
// material-ui components
import Box from "@mui/system/Box";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
// custom components
import DeleteModal from "../../../shared-components/crud/DeleteModal";
import FormSectionHeader from "../../../shared-components/crud/instance/FormSectionHeader";
import UserColorSelect from "./UserColorSelect";
import UserRoleSelect from "./UserRoleSelect";

// form validation
const schema = yup.object().shape({
  first_name: yup.string().required("You must enter a name"),
  last_name: yup.string().required("You must enter a last name"),
  email: yup.string().required("You must enter a last name"),
  role: yup.string().required("You must select a role"),
  organization: yup.string().required("You must select an organization"),
  title: yup.string().required("You must enter a title"),
  settings: yup.object().shape({
    color: yup.string().required("You must select a color"),
    dark_mode: yup.boolean(),
  }),
});

const UserForm = (props) => {
  const { instance, handleInstanceAction, loading, setOnSave, setSaveDisabled } = useOutletContext();
  const [organizations, setOrganizations] = useState(null);
  // dispatch
  const dispatch = useDispatch();
  // route params
  const routeParams = useParams();
  // form
  const { control, watch, reset, handleSubmit, formState, getData, setValue } =
    useForm({
      mode: "onChange",
      resolver: yupResolver(schema),
    });
  // form state
  const { isValid, dirtyFields, errors } = formState;
  // watch form
  const form = watch();
  // get organization list
  const handleGetOrganizations = async () => {
    try {
      const response = await dispatch(getListing('organizations')()).unwrap();
      setOrganizations(response);
    } catch (err) {
      console.log(err.message);
    }
  }
  useEffect(() => {
    // get users
    handleGetOrganizations();
  }, [dispatch, routeParams]);
  // Update the parent component with the form's validity and dirty state
  useEffect(() => {
    setSaveDisabled(_.isEmpty(dirtyFields) || !isValid);
  }, [formState]);
  // on state change update user
  function onSubmit(data) {
    let dataToSend = {...data}
    // remove white spaces
    if(dataToSend.password) {
        dataToSend.password = dataToSend.password.trim()
    }
    // dispatch add or update course
    if (routeParams.id === 'new') {
      handleInstanceAction(addInstance('users')(dataToSend), 'post');
    } else {
      // rip out password if it's empty so the backend does not recieve an empty string
      if(!dataToSend.password) {
          delete dataToSend.password;
      }
        
      handleInstanceAction(updateInstance('users')(data), 'patch');
    }
  }
  // on user change reset form
  useEffect(() => {
    let data = {
      ...instance,
      organization: instance.organization.id,
    }
    reset({ ...data });
  }, [instance, reset]);
  // handle remove user
  const handleRemoveUser = () => {
    handleInstanceAction(deleteInstance('users')(data), 'delete').then(() => {
      navigate("/users");
    });
  };
  // Pass the handleSubmit function to setOnSave
  useEffect(() => {
    setOnSave(() => handleSubmit(onSubmit));
  }, []);
  // if no user return loading
  if (_.isEmpty(form) || !instance || !organizations) {
    return <BASELoading />;
  }

  return (
    <div className="relative flex flex-col flex-auto">
      <div className="relative my-64 px-24 sm:px-32 max-w-4xl">
        <FormSectionHeader title="User Information" description="Enter the basic user details below." />
        <div className="mt-32 grid w-full gap-24 sm:grid-cols-4">
          <div className="sm:col-span-2">
            <Controller
              control={control}
              name="first_name"
              render={({ field }) => (
                <TextField
                  {...field}
                  label="First Name"
                  placeholder="First Name"
                  id="first_name"
                  error={!!errors.first_name}
                  helperText={errors?.first_name?.message}
                  variant="outlined"
                  size="small"
                  required
                  fullWidth
                />
              )}
            />
          </div>
          <div className="sm:col-span-2">
            <Controller
              control={control}
              name="last_name"
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Last Name"
                  placeholder="Last Name"
                  id="last_name"
                  error={!!errors.last_name}
                  helperText={errors?.last_name?.message}
                  variant="outlined"
                  size="small"
                  required
                  fullWidth
                />
              )}
            />
          </div>
          <div className="sm:col-span-2">
            <Controller
              control={control}
              name="email"
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Email"
                  type="email"
                  placeholder="Email"
                  id="email"
                  error={!!errors.email}
                  helperText={errors?.email?.message}
                  variant="outlined"
                  size="small"
                  required
                  fullWidth
                />
              )}
            />
          </div>
          {routeParams.id === 'new' ? (
                <div className="sm:col-span-2">
                <Controller
                  control={control}
                  name="password"
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Password"
                      placeholder="Password"
                      id="password"
                      error={!!errors.password}
                      helperText={errors?.password?.message}
                      variant="outlined"
                      size="small"
                      required
                      fullWidth
                    />
                    )}
                />
                </div>
            ) : (
                <div className="sm:col-span-2">
                <Controller
                  control={control}
                  name="password"
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Password"
                      placeholder="Password"
                      id="password"
                      error={!!errors.password}
                      helperText={errors?.password?.message}
                      variant="outlined"
                      size="small"
                      fullWidth
                    />
                    )}
                />
                </div>
            )
            }
          <div className="sm:col-span-2">
            <FormControl
              variant="outlined"
              fullWidth
              error={!!errors.organization}
              required
            >
              <InputLabel id="organization">Organization</InputLabel>
              <Controller
                control={control}
                name="organization"
                defaultValue=""
                render={({ field: { onChange, value } }) => (
                  <Select
                    value={value}
                    onChange={(e) => {
                      onChange(e);
                    }}
                    label="Organization"
                    placeholder="Organization"
                    id="organization"
                    variant="outlined"
                    required
                    fullWidth
                    size="small"
                  >
                    <MenuItem value="" disabled>No Organization Available</MenuItem>
                    {organizations.map((organization) => (
                      <MenuItem key={organization.id} value={organization.id}>
                        {organization.name}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
            </FormControl>
          </div>
          <div className="sm:col-span-2">
            <Controller
              control={control}
              name="title"
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Title"
                  placeholder="Title"
                  id="title"
                  error={!!errors.name}
                  helperText={errors?.name?.message}
                  variant="outlined"
                  size="small"
                  required
                  fullWidth
                />
              )}
            />
          </div>
        </div>
        <Divider className="my-40" />
        <FormSectionHeader title="Account Details" description="Enter the account details below." />
        <div className="mt-32 grid w-full gap-24 sm:grid-cols-4">
          <div className="sm:col-span-2">
            <Controller
              name="role"
              control={control}
              render={({ field }) => <UserRoleSelect className="mt-32" {...field} />}
            />
          </div>
          <div className="sm:col-span-2">
            <Controller
              name="settings.color"
              control={control}
              render={({ field }) => <UserColorSelect className="mt-32" {...field} />}
            />
          </div>
          <div className="sm:col-span-2">
            <Controller
              name="active"
              control={control}
              render={({ field: { onChange, value } }) => (
                <FormControlLabel
                  label="Active"
                  control={
                    <Switch
                      onChange={(ev) => {
                        onChange(ev.target.checked);
                      }}
                      checked={value}
                      name="active"
                    />
                  }
                />
              )}
            />
          </div>
          <div className="sm:col-span-2">
            <Controller
              name="internal"
              control={control}
              render={({ field: { onChange, value } }) => (
                <FormControlLabel
                  label="Internal"
                  control={
                    <Switch
                      onChange={(ev) => {
                        onChange(ev.target.checked);
                      }}
                      checked={value}
                      name="internal"
                    />
                  }
                />
              )}
            />
          </div>
        </div>
      </div>
      <Box
        className="flex items-center mt-40 py-14 pr-16 pl-4 sm:pr-48 sm:pl-36 border-t"
        sx={{ backgroundColor: "background.default" }}
      >
        {routeParams.id !== "new" && (
          <DeleteModal onDelete={handleRemoveUser} resource="user" />
        )}

        <Button
          component={Link}
          className="ml-auto"
          to="/users"
        >
          Cancel
        </Button>
        <Button
          className="ml-8"
          variant="contained"
          color="secondary"
          disabled={_.isEmpty(dirtyFields) || !isValid}
          onClick={handleSubmit(onSubmit)}
        >
          Save
        </Button>
      </Box>
    </div>
  );
};

export default UserForm;
